/**
 * FO-517 (spec 217) — de beslisregels van de gestructureerde sollicitatie-
 * beoordeling, als pure functies (geen I/O). De API levert de rijen aan, deze
 * module rekent: scores per groep, technische en gewogen totaalscore, sterke
 * punten / aandachtspunten / risico's / ontbrekende informatie (elk met
 * herleiding naar de bronregel), het systeemadvies, de consistentiecontroles,
 * de 2-van-3-voortgang van de second review, patroonsignalen in de werkhistorie,
 * de 48-uursdeadline en de salarisindicatie uit de functiematrix.
 *
 * Uitgangspunten uit de spec:
 * - N/B (score null) telt NOOIT mee, ook niet negatief.
 * - Het systeemadvies is ondersteunend, nooit het besluit; een must-have die niet
 *   voldaan is maakt AANNEMEN onmogelijk.
 * - Salaris uitsluitend uit de functiematrix; rekenen in centen (integers),
 *   pas bij de uitvoer naar een bedrag met 2 decimalen.
 */
import {
  GROTE_AFWIJKING_DREMPEL,
  NEGATIEVE_VERTREKREDENEN,
  BEOORDELING_WEGINGSGROEP,
  BEOORDELING_BRON_LABELS,
  BEOORDELING_ONDERDEEL,
  BEOORDELING_ONDERDEEL_LABELS,
  BEOORDELING_WEGINGSGROEP_LABELS,
  GESPREKSFASE_STANDAARD_BRON,
  ONDERBOUWING_MIN_TEKENS,
  STANDAARD_GESPREKSFASE,
  BEOORDELING_ZEKERHEID_LABELS,
  BEOORDELAAR_ROL_LABELS,
  KANDIDAAT_ADVIES_KEUZE_LABELS,
  VERTREK_REDEN_LABELS,
  type AnalysePunt,
  type AnalyseRisico,
  type BeoordelaarRol,
  type BeoordelingBron,
  type BeoordelingGespreksfase,
  type BeoordelingOnderdeel,
  type BeoordelingSignaal,
  type BeoordelingWeergaveModus,
  type BeoordelingWegingsgroep,
  type BeoordelingZekerheid,
  type ConsistentieMelding,
  type EisToetsUitkomst,
  type Herleiding,
  type KandidaatAdviesKeuze,
  type KandidaatAdviesSoort,
  type KandidaatAnalyseDTO,
  type KandidaatBeoordelingStatus,
  type KandidaatGesprekType,
  type KandidaatStatus,
  type OnderdeelNotities,
  type PatroonSignalen,
  type ReviewReactie,
  type ReviewVoortgang,
  type SalarisFactorSoort,
  type SystemAdviesDTO,
  type TechnischOordeel,
  type VacatureEisPrioriteit,
  type VertrekReden,
  type VertrekToon,
  type Wegingen,
} from '@streep/types'
import { utcToAmsterdamWallclock } from './amsterdam-tijd'

// ─── Invoer-vormen (los van Prisma) ─────────────────────────────────────────

export interface RegelIn {
  id: string
  naam: string
  criteriumId: string | null
  skillId: string | null
  onderdeel: BeoordelingOnderdeel
  wegingsgroep: BeoordelingWegingsgroep
  score: number | null
  onderbouwing: string | null
  bron: BeoordelingBron | null
  zekerheid: BeoordelingZekerheid | null
  signaal: BeoordelingSignaal | null
}

export interface BeoordelingIn {
  id: string
  beoordelaarId: string
  beoordelaarNaam: string | null
  rol: BeoordelaarRol
  status: KandidaatBeoordelingStatus
  technischOordeel: TechnischOordeel | null
  technischOordeelReden: string | null
  technischOordeelToelichting: string | null
  jobhopperOordeel: boolean
  jobhopperOnderbouwing: string | null
  regels: RegelIn[]
}

export interface EisIn {
  vacatureEisId: string
  naam: string
  prioriteit: VacatureEisPrioriteit
  uitkomst: EisToetsUitkomst
  bron: BeoordelingBron | null
}

export interface WerkgeverIn {
  id: string
  werkgever: string
  /** ISO-datum (jjjj-mm-dd of volledige ISO); null = onbekend. */
  van: string | null
  tot: string | null
  vertrekReden: VertrekReden | null
  vertrekToon: VertrekToon | null
}

export interface AdviesIn {
  id: string
  soort: KandidaatAdviesSoort
  auteurId: string | null
  rol: BeoordelaarRol | null
  keuze: KandidaatAdviesKeuze | null
  reactie: ReviewReactie | null
  alternatiefAdvies: KandidaatAdviesKeuze | null
  reageertOpId: string | null
  vervangen: boolean
}

export interface BesprekingIn {
  wegingsgroep: BeoordelingWegingsgroep | null
}

export interface InfoVerzoekIn {
  status: 'OPEN' | 'BEANTWOORD' | 'VERVALLEN'
}

export interface AanbiedingBandIn {
  salaris: number
  bandGoedgekeurd: boolean
}

export interface SalarisBandIn {
  min: number
  max: number
}

export interface AnalyseInvoer {
  beoordelingen: BeoordelingIn[]
  eisen: EisIn[]
  werkgevers: WerkgeverIn[]
  adviezen: AdviesIn[]
  besprekingen: BesprekingIn[]
  infoVerzoeken: InfoVerzoekIn[]
  wegingen: Wegingen
  /** Technisch profiel van de vacature (Skill.id); leeg = alle gescoorde vakgebieden. */
  relevanteSkillIds: string[]
  recruiterId: string | null
  reviewUitzondering: boolean
  aanbieding: AanbiedingBandIn | null
  band: SalarisBandIn | null
  /** Peildatum voor de werkhistorie (ISO). */
  nu: string
}

// ─── Kleine helpers ─────────────────────────────────────────────────────────

const ZEKER: ReadonlySet<BeoordelingZekerheid> = new Set(['BEWEZEN', 'AANNEMELIJK'])

function rond(n: number, decimalen = 2): number {
  const f = 10 ** decimalen
  return Math.round(n * f) / f
}

export function gemiddeldeScore(waarden: readonly number[]): number | null {
  if (waarden.length === 0) return null
  return rond(waarden.reduce((a, b) => a + b, 0) / waarden.length)
}

/**
 * Sleutel van een regel: `skill:<skillId>` of `crit:<criteriumId>`. Dezelfde
 * sleutel zit in de foutpaden van `valideerBeoordelingAfronden`, in
 * `zichtbareRegelSleutels` en (via `beoordelingRegelId`) in het DOM-id van de
 * regel in het formulier: API, precheck en springdoel lopen nooit uit elkaar.
 */
export function regelSleutel(r: Pick<RegelIn, 'id' | 'criteriumId' | 'skillId'>): string {
  return r.skillId ? `skill:${r.skillId}` : `crit:${r.criteriumId ?? r.id}`
}

function herleidingVanRegel(r: RegelIn, b: BeoordelingIn): Herleiding {
  const delen = [
    r.naam,
    BEOORDELAAR_ROL_LABELS[b.rol] + (b.beoordelaarNaam ? ` (${b.beoordelaarNaam})` : ''),
    r.score === null ? 'N/B' : `score ${r.score}`,
  ]
  if (r.bron) delen.push(BEOORDELING_BRON_LABELS[r.bron])
  if (r.zekerheid) delen.push(BEOORDELING_ZEKERHEID_LABELS[r.zekerheid])
  return { soort: 'REGEL', id: r.id, omschrijving: delen.join(' - ') }
}

/** Afgeronde beoordelingen tellen mee; concepten en vervangen versies niet. */
export function telbareBeoordelingen(beoordelingen: readonly BeoordelingIn[]): BeoordelingIn[] {
  return beoordelingen.filter((b) => b.status === 'AFGEROND')
}

function datumMs(iso: string): number {
  return new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso).getTime()
}

// ─── Scores ─────────────────────────────────────────────────────────────────

/**
 * Technische totaalscore: per vakgebied of technische pijler het gemiddelde over
 * de beoordelaars, daarna het gemiddelde daarover (N/B uitgesloten). De vaste
 * pijlers (criteria in wegingsgroep TECHNISCH: elektrisch, mechanisch, PLC, ...)
 * tellen altijd mee; het vacatureprofiel (`relevanteSkillIds`) beperkt alleen de
 * losse vakgebieden.
 */
export function technischeScore(
  beoordelingen: readonly BeoordelingIn[],
  relevanteSkillIds: readonly string[] = [],
  metPijlers = true,
): number | null {
  const perSkill = new Map<string, number[]>()
  for (const b of beoordelingen) {
    for (const r of b.regels) {
      if (r.score === null) continue
      if (r.skillId) {
        if (relevanteSkillIds.length > 0 && !relevanteSkillIds.includes(r.skillId)) continue
      } else if (!metPijlers || r.wegingsgroep !== 'TECHNISCH') continue
      const k = regelSleutel(r)
      const lijst = perSkill.get(k) ?? []
      lijst.push(r.score)
      perSkill.set(k, lijst)
    }
  }
  const gemiddelden = [...perSkill.values()].map((s) => s.reduce((a, c) => a + c, 0) / s.length)
  return gemiddeldeScore(gemiddelden)
}

/** Score per wegingsgroep (gemiddelde per criterium over beoordelaars, dan over criteria). */
export function scorePerGroep(
  beoordelingen: readonly BeoordelingIn[],
  relevanteSkillIds: readonly string[] = [],
): Record<BeoordelingWegingsgroep, number | null> {
  const perGroep = new Map<BeoordelingWegingsgroep, Map<string, number[]>>()
  for (const b of beoordelingen) {
    for (const r of b.regels) {
      if (r.score === null || r.skillId) continue
      const groep = perGroep.get(r.wegingsgroep) ?? new Map<string, number[]>()
      const k = regelSleutel(r)
      groep.set(k, [...(groep.get(k) ?? []), r.score])
      perGroep.set(r.wegingsgroep, groep)
    }
  }
  const uit = {} as Record<BeoordelingWegingsgroep, number | null>
  for (const g of BEOORDELING_WEGINGSGROEP) {
    const crit = perGroep.get(g)
    uit[g] = crit ? gemiddeldeScore([...crit.values()].map((s) => s.reduce((a, c) => a + c, 0) / s.length)) : null
  }
  // Technisch = de technische totaalscore (vakgebieden + technische pijlers,
  // elk als eigen onderdeel van het gemiddelde).
  uit.TECHNISCH = technischeScore(beoordelingen, relevanteSkillIds)
  return uit
}

/**
 * Gewogen totaalscore: alleen groepen met een score tellen, de wegingen worden
 * over die groepen genormaliseerd. Hulpmiddel, nooit een beslisregel (§F11).
 */
export function gewogenTotaal(
  perGroep: Record<BeoordelingWegingsgroep, number | null>,
  wegingen: Wegingen,
): number | null {
  let som = 0
  let gewicht = 0
  for (const g of BEOORDELING_WEGINGSGROEP) {
    const s = perGroep[g]
    const w = wegingen[g]
    if (s === null || w <= 0) continue
    som += s * w
    gewicht += w
  }
  return gewicht === 0 ? null : rond(som / gewicht)
}

/** Per groep het gemiddelde per beoordelaar; verschil >= drempel = grote afwijking. */
export function groteAfwijkingen(
  beoordelingen: readonly BeoordelingIn[],
  relevanteSkillIds: readonly string[] = [],
): Array<{ groep: BeoordelingWegingsgroep; min: number; max: number }> {
  const perBeoordelaar = new Map<string, BeoordelingIn[]>()
  for (const b of beoordelingen) {
    perBeoordelaar.set(b.beoordelaarId, [...(perBeoordelaar.get(b.beoordelaarId) ?? []), b])
  }
  if (perBeoordelaar.size < 2) return []
  const scores = new Map<BeoordelingWegingsgroep, number[]>()
  for (const lijst of perBeoordelaar.values()) {
    const g = scorePerGroep(lijst, relevanteSkillIds)
    for (const groep of BEOORDELING_WEGINGSGROEP) {
      const s = g[groep]
      if (s !== null) scores.set(groep, [...(scores.get(groep) ?? []), s])
    }
  }
  const uit: Array<{ groep: BeoordelingWegingsgroep; min: number; max: number }> = []
  for (const [groep, s] of scores) {
    if (s.length < 2) continue
    const min = Math.min(...s)
    const max = Math.max(...s)
    if (rond(max - min) >= GROTE_AFWIJKING_DREMPEL) uit.push({ groep, min, max })
  }
  return uit
}

// ─── Werkhistorie ───────────────────────────────────────────────────────────

function duurMaanden(w: WerkgeverIn, nu: string): number | null {
  if (!w.van) return null
  const eind = w.tot ? datumMs(w.tot) : datumMs(nu)
  const maanden = (eind - datumMs(w.van)) / (1000 * 60 * 60 * 24 * 30.4375)
  return maanden < 0 ? null : rond(maanden, 1)
}

/** Feitelijke patroonsignalen; nooit een oordeel (§D6). */
export function patroonSignalen(werkgevers: readonly WerkgeverIn[], nu: string): PatroonSignalen {
  const duren = werkgevers.map((w) => duurMaanden(w, nu)).filter((d): d is number => d !== null)
  const nuMs = datumMs(nu)
  const jaarMs = 365.25 * 24 * 60 * 60 * 1000
  const binnen = (jaren: number) =>
    werkgevers.filter((w) => {
      const eind = w.tot ? datumMs(w.tot) : nuMs
      return eind >= nuMs - jaren * jaarMs
    }).length
  const telling = new Map<VertrekReden, number>()
  for (const w of werkgevers) if (w.vertrekReden) telling.set(w.vertrekReden, (telling.get(w.vertrekReden) ?? 0) + 1)
  const vertrekredenen = [...telling.entries()]
    .map(([reden, aantal]) => ({ reden, aantal }))
    .sort((a, b) => b.aantal - a.aantal)
  const samenvatting =
    vertrekredenen.length === 0
      ? werkgevers.length === 0
        ? 'Geen werkhistorie vastgelegd.'
        : `${werkgevers.length} werkgever(s), vertrekredenen niet vastgelegd.`
      : vertrekredenen
          .map((v) => `${v.aantal} vertrek${v.aantal === 1 ? '' : 'ken'} wegens ${VERTREK_REDEN_LABELS[v.reden].toLowerCase()}`)
          .join(', ') + '.'
  return {
    aantalWerkgevers: werkgevers.length,
    gemiddeldeDuurMaanden: duren.length ? rond(duren.reduce((a, b) => a + b, 0) / duren.length, 1) : null,
    kortsteDuurMaanden: duren.length ? Math.min(...duren) : null,
    langsteDuurMaanden: duren.length ? Math.max(...duren) : null,
    werkgeversLaatste5Jaar: binnen(5),
    werkgeversLaatste10Jaar: binnen(10),
    vertrekredenen,
    samenvatting,
  }
}

function negatieveVertrekken(werkgevers: readonly WerkgeverIn[]): WerkgeverIn[] {
  return werkgevers.filter(
    (w) => (w.vertrekReden && NEGATIEVE_VERTREKREDENEN.includes(w.vertrekReden)) || w.vertrekToon === 'NEGATIEF',
  )
}

/** Stabiele historie: gemiddeld >= 24 maanden en geen negatieve vertrekken. */
export function isStabieleHistorie(werkgevers: readonly WerkgeverIn[], nu: string): boolean {
  const p = patroonSignalen(werkgevers, nu)
  return p.gemiddeldeDuurMaanden !== null && p.gemiddeldeDuurMaanden >= 24 && negatieveVertrekken(werkgevers).length === 0
}

// ─── Pro's, con's, risico's, ontbrekend ─────────────────────────────────────

interface Verzameld {
  naam: string
  herleiding: Herleiding[]
  scores: number[]
}

function verzamel(beoordelingen: readonly BeoordelingIn[], filter: (r: RegelIn) => boolean): Verzameld[] {
  const map = new Map<string, Verzameld>()
  for (const b of beoordelingen) {
    for (const r of b.regels) {
      if (!filter(r)) continue
      const k = regelSleutel(r)
      const v = map.get(k) ?? { naam: r.naam, herleiding: [], scores: [] }
      v.herleiding.push(herleidingVanRegel(r, b))
      if (r.score !== null) v.scores.push(r.score)
      map.set(k, v)
    }
  }
  return [...map.values()]
}

export function sterkePunten(beoordelingen: readonly BeoordelingIn[]): AnalysePunt[] {
  return verzamel(beoordelingen, (r) => r.score !== null && r.score >= 4 && !!r.zekerheid && ZEKER.has(r.zekerheid)).map(
    (v) => ({ tekst: `${v.naam}: sterk (gem. ${gemiddeldeScore(v.scores)})`, herleiding: v.herleiding }),
  )
}

export function aandachtspunten(beoordelingen: readonly BeoordelingIn[]): AnalysePunt[] {
  return verzamel(
    beoordelingen,
    (r) => r.signaal === 'AANDACHTSPUNT' || (r.score !== null && r.score <= 2 && !!r.onderbouwing?.trim()),
  ).map((v) => ({
    tekst: v.scores.length ? `${v.naam}: aandachtspunt (gem. ${gemiddeldeScore(v.scores)})` : `${v.naam}: aandachtspunt`,
    herleiding: v.herleiding,
  }))
}

export function risicos(
  beoordelingen: readonly BeoordelingIn[],
  eisen: readonly EisIn[],
  werkgevers: readonly WerkgeverIn[],
): AnalyseRisico[] {
  const uit: AnalyseRisico[] = []
  for (const v of verzamel(beoordelingen, (r) => r.signaal === 'SERIEUS_RISICO')) {
    uit.push({ niveau: 'RELEVANT', tekst: `${v.naam}: serieus risico`, herleiding: v.herleiding })
  }
  for (const e of eisen) {
    if (e.prioriteit === 'MUST_HAVE' && e.uitkomst === 'VOLDOET_NIET') {
      uit.push({
        niveau: 'RELEVANT',
        tekst: `Must-have niet voldaan: ${e.naam}`,
        herleiding: [{ soort: 'EIS', id: e.vacatureEisId, omschrijving: `${e.naam}${e.bron ? ` - ${BEOORDELING_BRON_LABELS[e.bron]}` : ''}` }],
      })
    }
  }
  const negatief = negatieveVertrekken(werkgevers)
  if (negatief.length > 0) {
    uit.push({
      niveau: negatief.length >= 2 ? 'AANDACHTSPUNT' : 'LAAG',
      tekst: `${negatief.length} vertrek(ken) met een negatieve reden of toon`,
      herleiding: negatief.map((w) => ({
        soort: 'WERKGEVER' as const,
        id: w.id,
        omschrijving: `${w.werkgever}${w.vertrekReden ? ` - ${VERTREK_REDEN_LABELS[w.vertrekReden]}` : ''}`,
      })),
    })
  }
  return uit
}

export function ontbrekendeInformatie(
  beoordelingen: readonly BeoordelingIn[],
  eisen: readonly EisIn[],
): AnalysePunt[] {
  const uit: AnalysePunt[] = verzamel(
    beoordelingen,
    (r) => r.score === null || r.zekerheid === 'NOG_TE_CONTROLEREN' || r.signaal === 'ONVOLDOENDE_INFORMATIE',
  ).map((v) => ({ tekst: `${v.naam}: niet beoordeeld of nog te controleren`, herleiding: v.herleiding }))
  for (const e of eisen) {
    if (e.uitkomst === 'ONBEKEND') {
      uit.push({
        tekst: `Eis nog niet getoetst: ${e.naam}`,
        herleiding: [{ soort: 'EIS', id: e.vacatureEisId, omschrijving: e.naam }],
      })
    }
  }
  return uit
}

// ─── Systeemadvies ──────────────────────────────────────────────────────────

function zekerheidsAandeel(beoordelingen: readonly BeoordelingIn[]): { aandeelZeker: number; aandeelOnbekend: number; totaal: number } {
  let totaal = 0
  let zeker = 0
  let onbekend = 0
  for (const b of beoordelingen) {
    for (const r of b.regels) {
      totaal++
      if (r.score === null || r.zekerheid === 'NOG_TE_CONTROLEREN') onbekend++
      else if (r.zekerheid && ZEKER.has(r.zekerheid)) zeker++
    }
  }
  return { aandeelZeker: totaal ? zeker / totaal : 0, aandeelOnbekend: totaal ? onbekend / totaal : 1, totaal }
}

/**
 * §E9 — systeemadvies volgens vaste, zichtbare regels (in volgorde):
 *  1. geen afgeronde beoordeling → AANVULLENDE_INFO
 *  2. must-have niet voldaan → AFWIJZEN (nooit AANNEMEN)
 *  3. grote verschillen tussen beoordelaars → HERBEOORDELING
 *  4. >= 30% N/B of "nog te controleren" → AANVULLENDE_INFO
 *  5. technisch >= 4 (overwegend bewezen/aannemelijk), geen relevant risico:
 *     totaal >= 3,5 en alle must-haves voldaan → AANNEMEN, anders TWEEDE_GESPREK
 *  6. technisch < 2,5 of totaal < 2,5 → AFWIJZEN
 *  7. overig → TWEEDE_GESPREK
 */
export const SYSTEEMADVIES_REGELS: readonly string[] = [
  'Geen afgeronde beoordeling: aanvullende informatie nodig.',
  'Must-have niet voldaan: afwijzen (nooit aannemen).',
  'Beoordelaars verschillen 1,5 punt of meer: herbeoordeling.',
  '30% of meer niet beoordeeld of nog te controleren: aanvullende informatie of technische verificatie.',
  'Technisch 4 of hoger (bewezen/aannemelijk) zonder relevante risico\'s: aannemen bij totaal 3,5+ en alle must-haves voldaan, anders tweede gesprek.',
  'Technisch of totaal onder 2,5: afwijzen.',
  'Overige gevallen: tweede gesprek.',
]

export function systeemAdvies(input: {
  beoordelingen: readonly BeoordelingIn[]
  eisen: readonly EisIn[]
  technisch: number | null
  totaal: number | null
  risicos: readonly AnalyseRisico[]
  sterk: readonly AnalysePunt[]
  aandacht: readonly AnalysePunt[]
  ontbrekend: readonly AnalysePunt[]
  afwijkingen: ReadonlyArray<{ groep: BeoordelingWegingsgroep }>
}): SystemAdviesDTO {
  const { beoordelingen, eisen, technisch, totaal } = input
  const relevant = input.risicos.filter((r) => r.niveau === 'RELEVANT')
  const z = zekerheidsAandeel(beoordelingen)
  const zekerheid: SystemAdviesDTO['zekerheid'] = z.aandeelZeker >= 0.7 ? 'HOOG' : z.aandeelZeker >= 0.4 ? 'MIDDEL' : 'LAAG'
  const basis = {
    argumentenVoor: [...input.sterk],
    argumentenTegen: [...input.aandacht, ...input.risicos],
    ontbrekend: [...input.ontbrekend],
  }
  const mustNiet = eisen.filter((e) => e.prioriteit === 'MUST_HAVE' && e.uitkomst === 'VOLDOET_NIET')
  const mustOnbekend = eisen.filter((e) => e.prioriteit === 'MUST_HAVE' && e.uitkomst === 'ONBEKEND')

  if (beoordelingen.length === 0) return { keuze: 'AANVULLENDE_INFO', zekerheid: 'LAAG', ...basis, regel: SYSTEEMADVIES_REGELS[0]! }
  if (mustNiet.length > 0) return { keuze: 'AFWIJZEN', zekerheid, ...basis, regel: SYSTEEMADVIES_REGELS[1]! }
  if (input.afwijkingen.length > 0) return { keuze: 'HERBEOORDELING', zekerheid, ...basis, regel: SYSTEEMADVIES_REGELS[2]! }
  if (z.aandeelOnbekend >= 0.3) return { keuze: 'AANVULLENDE_INFO', zekerheid, ...basis, regel: SYSTEEMADVIES_REGELS[3]! }
  if (technisch !== null && technisch >= 4 && z.aandeelZeker >= 0.5 && relevant.length === 0) {
    const aannemen = totaal !== null && totaal >= 3.5 && mustOnbekend.length === 0
    return { keuze: aannemen ? 'AANNEMEN' : 'TWEEDE_GESPREK', zekerheid, ...basis, regel: SYSTEEMADVIES_REGELS[4]! }
  }
  if ((technisch !== null && technisch < 2.5) || (totaal !== null && totaal < 2.5)) {
    return { keuze: 'AFWIJZEN', zekerheid, ...basis, regel: SYSTEEMADVIES_REGELS[5]! }
  }
  return { keuze: 'TWEEDE_GESPREK', zekerheid, ...basis, regel: SYSTEEMADVIES_REGELS[6]! }
}

// ─── Consistentiecontroles ──────────────────────────────────────────────────

export function consistentieControles(input: {
  beoordelingen: readonly BeoordelingIn[]
  eisen: readonly EisIn[]
  werkgevers: readonly WerkgeverIn[]
  besprekingen: readonly BesprekingIn[]
  afwijkingen: ReadonlyArray<{ groep: BeoordelingWegingsgroep; min: number; max: number }>
  relevanteSkillIds: readonly string[]
  aanbieding: AanbiedingBandIn | null
  band: SalarisBandIn | null
  nu: string
}): ConsistentieMelding[] {
  const uit: ConsistentieMelding[] = []
  const stabiel = isStabieleHistorie(input.werkgevers, input.nu)
  for (const b of input.beoordelingen) {
    const tech = technischeScore([b], input.relevanteSkillIds)
    if (b.technischOordeel === 'ONVOLDOENDE' && tech !== null && tech >= 4) {
      uit.push({
        code: 'TECHNISCH_STERK_MAAR_ONVOLDOENDE',
        tekst: `${BEOORDELAAR_ROL_LABELS[b.rol]} scoort technisch ${tech} maar concludeert "technisch onvoldoende".`,
        blokkeert: true,
        opgelost: technischeRedenCompleet(b),
        herleiding: [{ soort: 'BEOORDELING', id: b.id, omschrijving: `${BEOORDELAAR_ROL_LABELS[b.rol]}${b.beoordelaarNaam ? ` (${b.beoordelaarNaam})` : ''}` }],
      })
    }
    if (b.jobhopperOordeel && stabiel) {
      uit.push({
        code: 'JOBHOPPER_BIJ_STABIELE_HISTORIE',
        tekst: 'Oordeel "jobhopper" terwijl de historie stabiel is met normale vertrekredenen.',
        blokkeert: true,
        opgelost: (b.jobhopperOnderbouwing ?? '').trim().length >= 10,
        herleiding: [{ soort: 'BEOORDELING', id: b.id, omschrijving: BEOORDELAAR_ROL_LABELS[b.rol] }],
      })
    }
  }
  for (const a of input.afwijkingen) {
    const besproken = input.besprekingen.some((x) => x.wegingsgroep === null || x.wegingsgroep === a.groep)
    uit.push({
      code: 'GROTE_AFWIJKING_BEOORDELAARS',
      tekst: `Grote afwijking, bespreking vereist: ${BEOORDELING_WEGINGSGROEP_LABELS[a.groep]} ${a.min} tegen ${a.max}.`,
      blokkeert: true,
      opgelost: besproken,
      herleiding: input.beoordelingen.map((b) => ({ soort: 'BEOORDELING' as const, id: b.id, omschrijving: BEOORDELAAR_ROL_LABELS[b.rol] })),
    })
  }
  if (input.aanbieding && input.band && buitenBand(input.aanbieding.salaris, input.band)) {
    uit.push({
      code: 'BUITEN_SALARISBAND',
      tekst: `Aanbieding (${input.aanbieding.salaris}) valt buiten de band ${input.band.min} - ${input.band.max}: goedkeuring directie + onderbouwing verplicht.`,
      blokkeert: false,
      opgelost: input.aanbieding.bandGoedgekeurd,
      herleiding: [],
    })
  }
  for (const e of input.eisen) {
    if (e.prioriteit === 'MUST_HAVE' && e.uitkomst === 'VOLDOET_NIET') {
      uit.push({
        code: 'MUST_HAVE_NIET_VOLDAAN',
        tekst: `Must-have niet voldaan: ${e.naam}. Systeemadvies kan nooit "aannemen" zijn.`,
        blokkeert: false,
        opgelost: false,
        herleiding: [{ soort: 'EIS', id: e.vacatureEisId, omschrijving: e.naam }],
      })
    }
  }
  return uit
}

function technischeRedenCompleet(b: Pick<BeoordelingIn, 'technischOordeelReden' | 'technischOordeelToelichting'>): boolean {
  if (!b.technischOordeelReden?.trim()) return false
  if (b.technischOordeelReden === 'Anders') return (b.technischOordeelToelichting ?? '').trim().length >= 10
  return true
}

// ─── Afronden (FO-517 §C5/§E10, batch-70 FO-530 §F12) ─────────────────────

/** Een melding bij afronden: `path` = het veld (zie `springdoelVoorAfrondFout`). */
export interface AfrondFout {
  path: string
  message: string
}

export interface AfrondContext {
  werkgevers: readonly WerkgeverIn[]
  relevanteSkillIds: readonly string[]
  nu: string
  eigenOnderdelen: readonly BeoordelingOnderdeel[]
  /**
   * batch-70 (FO-530 §F12, oorzaak d) — de regels die het formulier TOONT
   * (actieve criteria in de gekozen onderdelen plus de vakgebieden als Technisch
   * gekozen is; zie `zichtbareRegelSleutels`). Andere regels tellen bij afronden
   * nooit mee: de beoordelaar kan ze niet zien en dus niet aanvullen.
   */
  zichtbareRegelSleutels: Iterable<string>
  /** §D7 / A4 — gespreksnotitie per onderdeel; onderbouwt een score 3 of 4. */
  onderdeelNotities?: OnderdeelNotities
}

/** Lengte van een onderbouwing zoals de regels hem tellen (zonder spaties aan de randen). */
export function onderbouwingTekens(tekst: string | null | undefined): number {
  return (tekst ?? '').trim().length
}

/**
 * A4 — wanneer een regel bij afronden een eigen onderbouwing (minimaal
 * `ONDERBOUWING_MIN_TEKENS`) nodig heeft:
 * - een signaal "aandachtspunt" of "serieus risico" (ook zonder score);
 * - score 1, 2 of 5 (de uitersten moeten herleidbaar zijn voor de second review);
 * - score 3 of 4 alleen als de bron "onvoldoende informatie" is en de
 *   gespreksnotitie van het onderdeel leeg is. Met een echte bron of een gevulde
 *   onderdeelnotitie volstaat de score.
 * Geeft de reden terug (voor de melding), of null als het niet verplicht is.
 */
export function onderbouwingPlicht(
  r: Pick<RegelIn, 'score' | 'bron' | 'signaal'>,
  onderdeelNotitieGevuld: boolean,
): 'SIGNAAL' | 'UITERSTE_SCORE' | 'ZONDER_BRON_OF_NOTITIE' | null {
  if (r.signaal === 'AANDACHTSPUNT' || r.signaal === 'SERIEUS_RISICO') return 'SIGNAAL'
  if (r.score === null) return null
  if (r.score <= 2 || r.score >= 5) return 'UITERSTE_SCORE'
  if (r.bron === 'ONVOLDOENDE_INFORMATIE' && !onderdeelNotitieGevuld) return 'ZONDER_BRON_OF_NOTITIE'
  return null
}

/**
 * §F12 — de regels die het formulier toont: elk ACTIEF criterium waarvan het
 * onderdeel gekozen is, plus (als Technisch gekozen is) de vakgebieden van het
 * formulier. API en frontend-precheck rekenen met dezelfde set.
 */
export function zichtbareRegelSleutels(input: {
  onderdelen: readonly BeoordelingOnderdeel[]
  actieveCriteria: ReadonlyArray<{ id: string; onderdeel: BeoordelingOnderdeel }>
  skillIds: Iterable<string>
}): Set<string> {
  const uit = new Set<string>()
  for (const c of input.actieveCriteria) {
    if (input.onderdelen.includes(c.onderdeel)) uit.add(`crit:${c.id}`)
  }
  if (input.onderdelen.includes('TECHNISCH')) {
    for (const s of input.skillIds) uit.add(`skill:${s}`)
  }
  return uit
}

/**
 * §C5 + §E10 — controle bij het afronden van een beoordeling. Geeft per veld
 * een melding (inline in het formulier), leeg = afronden mag. Gedeeld door de
 * API (`POST .../afronden`) en de precheck in het formulier: wat de knop vooraf
 * meldt is precies wat de server weigert.
 *
 * batch-70 (FO-530 §F12): alleen zichtbare regels tellen (oorzaak d), de
 * onderbouwingsplicht volgt `onderbouwingPlicht` (A4, oorzaak a/b), een score
 * zonder bron of een lage score op "onvoldoende informatie" blijft een fout
 * (oorzaak c) en elk pad heeft een springdoel (`springdoelVoorAfrondFout`, oorzaak e).
 */
export function valideerBeoordelingAfronden(b: BeoordelingIn, context: AfrondContext): AfrondFout[] {
  const fouten: AfrondFout[] = []
  const zichtbaar = new Set(context.zichtbareRegelSleutels)
  const regels = b.regels.filter((r) => zichtbaar.has(regelSleutel(r)))
  const eigen = regels.filter((r) => context.eigenOnderdelen.includes(r.onderdeel))
  if (!eigen.some((r) => r.score !== null)) {
    fouten.push({ path: 'regels', message: 'Geef in je eigen onderdelen minimaal een score (of zet een criterium bewust op N/B met toelichting).' })
  }
  // Per regel: tijdens het gesprek mocht alleen een score; nu moet het compleet.
  for (const r of regels) {
    const sleutel = regelSleutel(r)
    if (r.score !== null) {
      if (!r.bron) fouten.push({ path: `regel.${sleutel}.bron`, message: `${r.naam}: geef de bron van de score.` })
      else if (r.score <= 2 && r.bron === 'ONVOLDOENDE_INFORMATIE') {
        fouten.push({ path: `regel.${sleutel}.bron`, message: `${r.naam}: een score ${r.score} kan niet op onvoldoende informatie rusten. Kies N/B of een echte bron.` })
      }
    }
    const notitieGevuld = onderbouwingTekens(context.onderdeelNotities?.[r.onderdeel]) >= ONDERBOUWING_MIN_TEKENS
    const plicht = onderbouwingPlicht(r, notitieGevuld)
    if (plicht && onderbouwingTekens(r.onderbouwing) < ONDERBOUWING_MIN_TEKENS) {
      const message =
        plicht === 'SIGNAAL'
          ? `${r.naam}: onderbouw het ${r.signaal === 'SERIEUS_RISICO' ? 'risico' : 'aandachtspunt'} (minimaal ${ONDERBOUWING_MIN_TEKENS} tekens).`
          : plicht === 'UITERSTE_SCORE'
            ? `${r.naam}: een score ${r.score} vraagt een korte onderbouwing (minimaal ${ONDERBOUWING_MIN_TEKENS} tekens).`
            : `${r.naam}: score ${r.score} op "onvoldoende informatie" vraagt een onderbouwing of een gespreksnotitie bij ${BEOORDELING_ONDERDEEL_LABELS[r.onderdeel].toLowerCase()}.`
      fouten.push({ path: `regel.${sleutel}.onderbouwing`, message })
    }
  }
  const tech = technischeScore([{ ...b, regels }], context.relevanteSkillIds)
  if (b.technischOordeel === 'ONVOLDOENDE' && tech !== null && tech >= 4 && !technischeRedenCompleet(b)) {
    fouten.push({ path: 'technischOordeelReden', message: `Technisch scoort ${tech}, maar de conclusie is "onvoldoende": kies een reden uit de lijst (bij "Anders" met toelichting).` })
  }
  if (b.jobhopperOordeel && isStabieleHistorie(context.werkgevers, context.nu) && (b.jobhopperOnderbouwing ?? '').trim().length < 10) {
    fouten.push({ path: 'jobhopperOnderbouwing', message: 'De werkhistorie is stabiel met normale vertrekredenen: onderbouw het oordeel "jobhopper".' })
  }
  return fouten
}

// ─── Springdoelen in het formulier (FO-530 §F12, oorzaak e) ────────────────

/** DOM-id van een onderdeel-sectie in het formulier. */
export function beoordelingSectieId(onderdeel: BeoordelingOnderdeel | 'AANVULLEN'): string {
  return `sectie-${onderdeel}`
}

/** DOM-id van een regel in het formulier (sleutel uit `regelSleutel`). */
export function beoordelingRegelId(sleutel: string): string {
  return `regel-${sleutel}`
}

/** Velden buiten de regels die een afrond-melding kunnen krijgen. */
export const BEOORDELING_LOSSE_VELDEN = ['technischOordeel', 'technischOordeelReden', 'jobhopperOnderbouwing'] as const
export type BeoordelingLosVeld = (typeof BEOORDELING_LOSSE_VELDEN)[number]

/** DOM-id van een los veld in het formulier. */
export function beoordelingVeldId(veld: BeoordelingLosVeld): string {
  return `veld-${veld}`
}

export interface AfrondSpringdoel {
  /** Het element waar de klik naartoe scrolt. */
  elementId: string
  /** Deze sectie moet open staan (anders bestaat het element niet). */
  sectie: BeoordelingOnderdeel | 'AANVULLEN'
  /** Deze regel moet zijn details (notitie, bron, ...) tonen. */
  regelSleutel: string | null
  /** Het veld binnen de regel of het losse veld. */
  veld: string | null
}

const VELD_SECTIE: Record<BeoordelingLosVeld, BeoordelingOnderdeel | 'AANVULLEN'> = {
  technischOordeel: 'TECHNISCH',
  technischOordeelReden: 'TECHNISCH',
  jobhopperOnderbouwing: 'AANVULLEN',
}

/**
 * Elk pad uit `valideerBeoordelingAfronden` krijgt een springdoel dat in het
 * formulier bestaat (oorzaak e: `veld-regels` had geen element). `regels` springt
 * naar het eerste eigen onderdeel; een regelfout naar de regel (met de notitie
 * open); een los veld naar zijn veld (met de sectie open). Null alleen voor een
 * onbekend pad of een regel die niet in het formulier staat.
 */
export function springdoelVoorAfrondFout(
  path: string,
  context: {
    eigenOnderdelen: readonly BeoordelingOnderdeel[]
    regels: ReadonlyArray<{ sleutel: string; onderdeel: BeoordelingOnderdeel }>
  },
): AfrondSpringdoel | null {
  if (path === 'regels') {
    const eerste = BEOORDELING_ONDERDEEL.find((o) => context.eigenOnderdelen.includes(o))
    if (!eerste) return null
    return { elementId: beoordelingSectieId(eerste), sectie: eerste, regelSleutel: null, veld: null }
  }
  const m = /^regel\.((?:skill|crit):[^.]+)(?:\.(\w+))?$/.exec(path)
  if (m) {
    const regel = context.regels.find((r) => r.sleutel === m[1])
    if (!regel) return null
    return { elementId: beoordelingRegelId(regel.sleutel), sectie: regel.onderdeel, regelSleutel: regel.sleutel, veld: m[2] ?? 'onderbouwing' }
  }
  const veld = (BEOORDELING_LOSSE_VELDEN as readonly string[]).includes(path) ? (path as BeoordelingLosVeld) : null
  if (veld) return { elementId: beoordelingVeldId(veld), sectie: VELD_SECTIE[veld], regelSleutel: null, veld }
  return null
}

// ─── Gespreksmodus, fase en voortgang (FO-530 §D, §E) ──────────────────────

/** Kandidaatstatussen waarin het gesprek (nog) loopt: daar staat de gespreksmodus standaard aan. */
export const GESPREKSFASE_STATUSSEN: readonly KandidaatStatus[] = [
  'PRE_SCREENING', 'GESPREK_GEPLAND', 'EERSTE_GESPREK', 'TWEEDE_GESPREK', 'GESPREK_GEHAD',
]

/** §D7 — gespreksmodus zolang de beoordeling concept is en de kandidaat in een gespreksfase zit. */
export function standaardWeergaveModus(
  kandidaatStatus: KandidaatStatus,
  beoordelingStatus: KandidaatBeoordelingStatus,
): BeoordelingWeergaveModus {
  return beoordelingStatus === 'CONCEPT' && GESPREKSFASE_STATUSSEN.includes(kandidaatStatus) ? 'GESPREK' : 'UITWERK'
}

/** A3 — de standaardbron van een score in deze fase (zonder fase: Interview). */
export function standaardBronVoorFase(fase: BeoordelingGespreksfase | null | undefined): BeoordelingBron {
  return GESPREKSFASE_STANDAARD_BRON[fase ?? STANDAARD_GESPREKSFASE]
}

/** Voortgang per onderdeel voor de sectienavigatie ("3/9"): gescoorde regels van het totaal. */
export function voortgangPerOnderdeel(
  regels: ReadonlyArray<{ onderdeel: BeoordelingOnderdeel; score: number | null }>,
): Record<BeoordelingOnderdeel, { gescoord: number; totaal: number }> {
  const uit = Object.fromEntries(BEOORDELING_ONDERDEEL.map((o) => [o, { gescoord: 0, totaal: 0 }])) as Record<
    BeoordelingOnderdeel,
    { gescoord: number; totaal: number }
  >
  for (const r of regels) {
    uit[r.onderdeel].totaal++
    if (r.score !== null) uit[r.onderdeel].gescoord++
  }
  return uit
}

/** Een gesprek dat binnen dit aantal minuten begint, telt als "nu bezig". */
const GESPREK_LOOPT_MARGE_MIN = 60

/** Een gesprek zonder tijdstip (alleen een dag, zo legt het gespreksformulier hem vast) staat op UTC-middernacht. */
function gesprekZonderTijd(iso: string): boolean {
  const d = new Date(iso)
  return d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0
}

/**
 * §E10 — loopt het gesprek al, of is het geweest? Met tijdstip (bv. na
 * verplaatsen): vanaf een uur vóór de start. Zonder tijdstip (alleen een dag):
 * een eerdere dag wel; de dag zelf alleen als er al gescoord wordt
 * (`metScore`). Wie 's ochtends het formulier voorbereidt voor een gesprek om
 * 15:00, zet de kandidaat dus nog niet op "gesprek gehad"; de eerste score doet
 * dat wel. "Vandaag" is de Amsterdamse kalenderdag.
 */
export function gesprekBegonnen(datum: string, nu: string, metScore: boolean): boolean {
  if (!gesprekZonderTijd(datum)) return datumMs(datum) <= datumMs(nu) + GESPREK_LOOPT_MARGE_MIN * 60_000
  const dag = datum.slice(0, 10)
  const vandaag = utcToAmsterdamWallclock(new Date(nu)).ymd
  return dag < vandaag || (dag === vandaag && metScore)
}

export interface StartFaseUitkomst {
  fase: BeoordelingGespreksfase
  /** De statusovergang die bij het starten hoort (alleen recruiter), of null. */
  statusNaar: KandidaatStatus | null
}

/**
 * §E10 — de fase en eventuele statusovergang bij het starten van een
 * beoordeling. Vanuit een gepland of gehouden gesprek: Interview, en de
 * recruiter zet de kandidaat van "gesprek gepland"/"eerste gesprek" op "gesprek
 * gehad" (het bestaande pad "Gesprek gevoerd" -> recruiterbeoordeling blijft de
 * stap daarna). Vanuit een telefonische prescreening (gesprekstype Telefonisch,
 * of nog geen gesprek in de instroom): Telefonisch, status pre-screening.
 *
 * De statussprong volgt het LAATSTE eerste gesprek (type EERSTE_GESPREK): een nieuw
 * gepland eerste gesprek houdt hem tegen, ook als er al een eerder was; een al
 * geplande vervolgafspraak (tweede gesprek, assessment, proefdag) niet. Een gesprek dat pas later begint
 * zet de status nooit vooruit; voor een gesprek zonder tijdstip op de dag zelf
 * is een score nodig (`metScore`, zie `gesprekBegonnen`). Dezelfde regel draait
 * bij het openen (zonder score) en bij het opslaan (met score).
 */
export function bepaalStartFase(input: {
  rol: BeoordelaarRol
  status: KandidaatStatus
  gesprekken: ReadonlyArray<{ type: KandidaatGesprekType; datum: string }>
  nu: string
  gekozenFase?: BeoordelingGespreksfase | null
  /** Er staat al minstens één score in de beoordeling (opslaan tijdens het gesprek). */
  metScore?: boolean
}): StartFaseUitkomst {
  const gesorteerd = [...input.gesprekken].sort((a, b) => datumMs(a.datum) - datumMs(b.datum))
  // Voor de fase telt het gesprek van vandaag mee, ook zonder score: het is het gesprek waarvoor het formulier opengaat.
  const opOfVoorVandaag = gesorteerd.filter((g) => gesprekBegonnen(g.datum, input.nu, true))
  const relevant = opOfVoorVandaag.at(-1) ?? gesorteerd[0] ?? null
  const instroom = (['NIEUW', 'NABELLEN', 'NIET_BEREIKBAAR', 'PRE_SCREENING'] as readonly KandidaatStatus[]).includes(input.status)
  const afgeleid: BeoordelingGespreksfase =
    relevant?.type === 'TELEFONISCH' || (!relevant && instroom)
      ? 'TELEFONISCH'
      : relevant?.type === 'TWEEDE_GESPREK' || input.status === 'TWEEDE_GESPREK'
        ? 'TWEEDE_GESPREK'
        : 'INTERVIEW'
  const fase = input.gekozenFase ?? afgeleid
  if (input.rol !== 'RECRUITER') return { fase, statusNaar: null }
  if (fase === 'TELEFONISCH' && (input.status === 'NIEUW' || input.status === 'NABELLEN')) {
    return { fase, statusNaar: 'PRE_SCREENING' }
  }
  if (fase === 'INTERVIEW' && (input.status === 'GESPREK_GEPLAND' || input.status === 'EERSTE_GESPREK')) {
    // Alleen als het laatste eerste gesprek loopt of geweest is (of er geen vastligt).
    const laatste = gesorteerd.filter((g) => g.type === 'EERSTE_GESPREK').at(-1)
    const gesprekGestart = !laatste || gesprekBegonnen(laatste.datum, input.nu, input.metScore ?? false)
    return { fase, statusNaar: gesprekGestart ? 'GESPREK_GEHAD' : null }
  }
  return { fase, statusNaar: null }
}

// ─── Second review: 2 van 3 ─────────────────────────────────────────────────

/** Het actuele recruiteradvies (niet vervangen, geen reactie op een afwijking). */
export function actueelRecruiterAdvies(adviezen: readonly AdviesIn[]): AdviesIn | null {
  return adviezen.filter((a) => a.soort === 'RECRUITER' && !a.vervangen && !a.reageertOpId && a.keuze).at(-1) ?? null
}

/**
 * Actuele second-review-reacties op het actuele recruiteradvies, één per
 * persoon en rol. Reviews op een eerder advies tellen niet mee.
 */
function reviewReacties(adviezen: readonly AdviesIn[], recruiterId: string | null): AdviesIn[] {
  const huidig = actueelRecruiterAdvies(adviezen)
  if (!huidig) return []
  // §5.2 — wie het recruiteradvies schreef (nu of eerder) telt maar een keer:
  // zijn advies telt al, een review van hem telt niet nog eens mee.
  const recruiters = new Set(adviezen.filter((a) => a.soort === 'RECRUITER' && a.auteurId).map((a) => a.auteurId as string))
  if (recruiterId) recruiters.add(recruiterId)
  const gezien = new Set<string>()
  const rollen = new Set<BeoordelaarRol>()
  const uit: AdviesIn[] = []
  for (const a of adviezen) {
    if (a.soort !== 'BEOORDELAAR' || a.vervangen || !a.rol || !a.auteurId) continue
    if (a.reactie !== 'VOLGEN' && a.reactie !== 'AFWIJKEN') continue
    if (a.reageertOpId !== huidig.id) continue
    if (recruiters.has(a.auteurId)) continue
    if (gezien.has(a.auteurId) || rollen.has(a.rol)) continue
    gezien.add(a.auteurId)
    rollen.add(a.rol)
    uit.push(a)
  }
  return uit
}

/** Afwijkingen waarop de recruiter nog niet gereageerd heeft (§G15). */
export function openAfwijkingen(adviezen: readonly AdviesIn[]): AdviesIn[] {
  const beantwoord = new Set(adviezen.filter((a) => a.soort === 'RECRUITER' && a.reageertOpId).map((a) => a.reageertOpId))
  return adviezen.filter((a) => a.soort === 'BEOORDELAAR' && !a.vervangen && a.reactie === 'AFWIJKEN' && !beantwoord.has(a.id))
}

export function reviewVoortgang(input: {
  adviezen: readonly AdviesIn[]
  recruiterId: string | null
  infoVerzoeken: readonly InfoVerzoekIn[]
  onbesprokenAfwijkingen: number
  blokkerendeConsistentie: number
  reviewUitzondering: boolean
}): ReviewVoortgang {
  const reacties = reviewReacties(input.adviezen, input.recruiterId)
  const rollenKlaar = reacties.map((r) => r.rol).filter((r): r is BeoordelaarRol => r !== null)
  // §5.3 — uitzondering "1 van 2 + directie": een second review van HR of TL
  // volstaat, het besluit ligt dan bij de directie (besluitnemers).
  const vereist = input.reviewUitzondering ? 1 : 2
  const compleet = input.reviewUitzondering
    ? rollenKlaar.some((r) => r === 'HR' || r === 'TL')
    : rollenKlaar.length >= vereist
  const recruiterAdvies = actueelRecruiterAdvies(input.adviezen) !== null
  const openInfo = input.infoVerzoeken.filter((i) => i.status === 'OPEN').length
  const openAfw = openAfwijkingen(input.adviezen).length
  const blokkades: string[] = []
  if (!recruiterAdvies) blokkades.push('Het recruiteradvies is nog niet afgerond.')
  if (!compleet) {
    blokkades.push(
      input.reviewUitzondering && rollenKlaar.length > 0
        ? 'Second review: bij de uitzondering is een review van HR of de teamleider nodig.'
        : `Second review: ${rollenKlaar.length} van ${vereist} beoordelingen binnen.`,
    )
  }
  if (openInfo > 0) blokkades.push(`${openInfo} open informatieverzoek(en).`)
  if (openAfw > 0) blokkades.push(`${openAfw} afwijking(en) wacht(en) op een reactie van de recruiter.`)
  if (input.onbesprokenAfwijkingen > 0) blokkades.push('Grote afwijking tussen beoordelaars: leg eerst een besprekingsnotitie vast.')
  if (input.blokkerendeConsistentie > 0) blokkades.push('Een consistentiecontrole vraagt nog een onderbouwing.')
  return {
    rollenKlaar,
    aantalKlaar: rollenKlaar.length,
    vereist,
    compleet,
    openInfoVerzoeken: openInfo,
    onbesprokenAfwijkingen: input.onbesprokenAfwijkingen,
    besluitMogelijk: blokkades.length === 0,
    blokkades,
  }
}

// ─── Totaalanalyse ──────────────────────────────────────────────────────────

export function analyseerKandidaat(input: AnalyseInvoer): KandidaatAnalyseDTO {
  const telbaar = telbareBeoordelingen(input.beoordelingen)
  const perGroep = scorePerGroep(telbaar, input.relevanteSkillIds)
  const technisch = technischeScore(telbaar, input.relevanteSkillIds)
  const totaal = gewogenTotaal(perGroep, input.wegingen)
  const sterk = sterkePunten(telbaar)
  const aandacht = aandachtspunten(telbaar)
  const risico = risicos(telbaar, input.eisen, input.werkgevers)
  const ontbrekend = ontbrekendeInformatie(telbaar, input.eisen)
  const afwijkingen = groteAfwijkingen(telbaar, input.relevanteSkillIds)
  const consistentie = consistentieControles({
    beoordelingen: telbaar,
    eisen: input.eisen,
    werkgevers: input.werkgevers,
    besprekingen: input.besprekingen,
    afwijkingen,
    relevanteSkillIds: input.relevanteSkillIds,
    aanbieding: input.aanbieding,
    band: input.band,
    nu: input.nu,
  })
  const onbesproken = consistentie.filter((c) => c.code === 'GROTE_AFWIJKING_BEOORDELAARS' && !c.opgelost).length
  const blokkerend = consistentie.filter((c) => c.blokkeert && !c.opgelost && c.code !== 'GROTE_AFWIJKING_BEOORDELAARS').length
  return {
    scorePerGroep: perGroep,
    technischeScore: technisch,
    totaalScore: totaal,
    sterkePunten: sterk,
    aandachtspunten: aandacht,
    risicos: risico,
    ontbrekend,
    systeemAdvies: systeemAdvies({ beoordelingen: telbaar, eisen: input.eisen, technisch, totaal, risicos: risico, sterk, aandacht, ontbrekend, afwijkingen }),
    consistentie,
    patroon: patroonSignalen(input.werkgevers, input.nu),
    review: reviewVoortgang({
      adviezen: input.adviezen,
      recruiterId: input.recruiterId,
      infoVerzoeken: input.infoVerzoeken,
      onbesprokenAfwijkingen: onbesproken,
      blokkerendeConsistentie: blokkerend,
      reviewUitzondering: input.reviewUitzondering,
    }),
    groteAfwijkingen: afwijkingen,
  }
}

// ─── 48-uursdeadline ────────────────────────────────────────────────────────

export type DeadlineFase = 'OK' | 'HERINNERING' | 'ESCALATIE'

/** Fase van de recruiterbeoordeling: herinnering na X uur, escalatie na de deadline. */
export function recruiterDeadlineFase(gesprekGehadOp: Date, nu: Date, herinneringUren: number, deadlineUren: number): DeadlineFase {
  const uren = (nu.getTime() - gesprekGehadOp.getTime()) / 3_600_000
  if (uren >= deadlineUren) return 'ESCALATIE'
  if (uren >= herinneringUren) return 'HERINNERING'
  return 'OK'
}

// ─── Salarisindicatie ───────────────────────────────────────────────────────

export function salarisNaarCenten(v: number | string): number {
  const n = typeof v === 'string' ? Number(v) : v
  return Math.round(n * 100)
}

export function salarisCentenNaarBedrag(c: number): string {
  const neg = c < 0
  const abs = Math.abs(c)
  return `${neg ? '-' : ''}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`
}

export function buitenBand(salaris: number, band: SalarisBandIn): boolean {
  const s = salarisNaarCenten(salaris)
  return s < salarisNaarCenten(band.min) || s > salarisNaarCenten(band.max)
}

export interface SchaalIn {
  functiegroep: string
  niveau: number
  min: string | number
  max: string | number
  adviesStart: string | number
}

export interface FactorIn {
  soort: SalarisFactorSoort
  label: string
  vanafWaarde: string | number
  opslag: string | number
}

export interface SalarisIndicatieUitkomst {
  functiegroep: string
  niveau: number
  schaalMin: string
  schaalMax: string
  adviesStart: string
  factoren: Array<{ soort: SalarisFactorSoort; label: string; waarde: number; opslag: string }>
  onderbouwing: string
  wens: string | null
  verschil: string | null
}

/**
 * §H19 — salarisindicatie uit de functiematrix. Niveau = opgegeven, anders het
 * hoogste niveau van de functiegroep dat <= de afgeronde technische score ligt
 * (minimaal het laagste niveau). Per factorsoort telt de hoogste trede waarvan
 * `vanafWaarde` <= de kandidaatwaarde. Het advies blijft binnen de schaal.
 * Retourneert null als de functiegroep niet in de matrix staat.
 */
export function normaliseerFunctiegroep(naam: string): string {
  return naam.trim().replace(/\s+/g, ' ').toLocaleLowerCase('nl-NL')
}

export function berekenSalarisIndicatie(input: {
  schalen: readonly SchaalIn[]
  factoren: readonly FactorIn[]
  functiegroep: string
  niveau: number | null
  technischeScore: number | null
  waarden: Partial<Record<SalarisFactorSoort, number | null>>
  wens: number | string | null
  matrixVersie: string
  matrixIngangsdatum: string
}): SalarisIndicatieUitkomst | null {
  // Hoofdletters en spaties maken geen verschil: "storingsmonteur" = "Storingsmonteur".
  const zoek = normaliseerFunctiegroep(input.functiegroep)
  const groep = input.schalen.filter((s) => normaliseerFunctiegroep(s.functiegroep) === zoek).sort((a, b) => a.niveau - b.niveau)
  if (groep.length === 0) return null
  let schaal: SchaalIn | undefined
  if (input.niveau !== null) schaal = groep.find((s) => s.niveau === input.niveau)
  if (!schaal) {
    const doel = input.technischeScore === null ? groep[0]!.niveau : Math.round(input.technischeScore)
    schaal = [...groep].reverse().find((s) => s.niveau <= doel) ?? groep[0]!
  }
  const min = salarisNaarCenten(schaal.min)
  const max = salarisNaarCenten(schaal.max)
  let advies = salarisNaarCenten(schaal.adviesStart)
  const toegepast: SalarisIndicatieUitkomst['factoren'] = []
  const soorten = [...new Set(input.factoren.map((f) => f.soort))]
  for (const soort of soorten) {
    const waarde = input.waarden[soort]
    if (waarde === null || waarde === undefined) continue
    const trede = input.factoren
      .filter((f) => f.soort === soort && Number(f.vanafWaarde) <= waarde)
      .sort((a, b) => Number(b.vanafWaarde) - Number(a.vanafWaarde))[0]
    if (!trede) continue
    const opslag = salarisNaarCenten(trede.opslag)
    advies += opslag
    toegepast.push({ soort, label: trede.label, waarde, opslag: salarisCentenNaarBedrag(opslag) })
  }
  const begrensd = advies > max ? 'max' : advies < min ? 'min' : null
  advies = Math.min(max, Math.max(min, advies))
  const wensC = input.wens === null || input.wens === '' ? null : salarisNaarCenten(input.wens)
  const regels = [
    `Functiematrix ${input.matrixVersie} (ingang ${input.matrixIngangsdatum}), functiegroep ${schaal.functiegroep} niveau ${schaal.niveau}: schaal ${salarisCentenNaarBedrag(min)} - ${salarisCentenNaarBedrag(max)}, advies-start ${salarisCentenNaarBedrag(salarisNaarCenten(schaal.adviesStart))}.`,
    ...toegepast.map((f) => `${f.label} (waarde ${f.waarde}): ${f.opslag.startsWith('-') ? '' : '+'}${f.opslag}.`),
  ]
  if (begrensd) regels.push(`Advies begrensd op het schaal${begrensd === 'max' ? 'maximum' : 'minimum'}.`)
  return {
    functiegroep: schaal.functiegroep,
    niveau: schaal.niveau,
    schaalMin: salarisCentenNaarBedrag(min),
    schaalMax: salarisCentenNaarBedrag(max),
    adviesStart: salarisCentenNaarBedrag(advies),
    factoren: toegepast,
    onderbouwing: regels.join('\n'),
    wens: wensC === null ? null : salarisCentenNaarBedrag(wensC),
    verschil: wensC === null ? null : salarisCentenNaarBedrag(wensC - advies),
  }
}

/** Afgeleide factorwaarden uit de beoordeling (geen Employee-salarissen, §H18). */
export function salarisFactorWaarden(input: {
  beoordelingen: readonly BeoordelingIn[]
  relevanteSkillIds: readonly string[]
  werkgevers: readonly WerkgeverIn[]
  ploegenInzetbaar: boolean | null
  aantalCertificaten: number
  storingsSkillIds: readonly string[]
  nu: string
}): Partial<Record<SalarisFactorSoort, number | null>> {
  const telbaar = telbareBeoordelingen(input.beoordelingen)
  const naamGem = (patroon: RegExp): number | null => {
    const s: number[] = []
    for (const b of telbaar) for (const r of b.regels) if (r.score !== null && patroon.test(r.naam)) s.push(r.score)
    return gemiddeldeScore(s)
  }
  const naamGemCriteria = (patroon: RegExp): number | null => {
    const s: number[] = []
    for (const b of telbaar) for (const r of b.regels) if (!r.skillId && r.score !== null && patroon.test(r.naam)) s.push(r.score)
    return gemiddeldeScore(s)
  }
  const maanden = input.werkgevers.map((w) => duurMaanden(w, input.nu) ?? 0).reduce((a, b) => a + b, 0)
  const specialisaties = new Set<string>()
  for (const b of telbaar) for (const r of b.regels) if (r.skillId && r.score !== null && r.score >= 4) specialisaties.add(r.skillId)
  return {
    TECHNISCH_NIVEAU: technischeScore(telbaar, input.relevanteSkillIds),
    ERVARING_JAREN: input.werkgevers.length ? rond(maanden / 12, 1) : null,
    ZELFSTANDIGHEID: naamGem(/zelfstandig/i),
    // Storings-vakgebieden plus de pijler "Storingen" (nooit de andere pijlers).
    STORINGSVAARDIGHEID: input.storingsSkillIds.length
      ? gemiddeldeScore([technischeScore(telbaar, input.storingsSkillIds, false), naamGemCriteria(/storing/i)].filter((x): x is number => x !== null))
      : naamGem(/storing/i),
    VERANTWOORDELIJKHEID: naamGem(/verantwoordelijk/i),
    SPECIALISATIE: specialisaties.size,
    PLOEGEN: input.ploegenInzetbaar === null ? null : input.ploegenInzetbaar ? 1 : 0,
    CERTIFICERING: input.aantalCertificaten,
  }
}

/** A1 — migratie van de oude skill-score 1-10 naar de schaal 1-5. */
export function skillScore10Naar5(score: number): number {
  return Math.min(5, Math.max(1, Math.round(score / 2)))
}

/** Label voor een adviesknop/regel (hergebruikt door UI en meldingen). */
export function adviesLabel(keuze: KandidaatAdviesKeuze | null): string {
  return keuze ? KANDIDAAT_ADVIES_KEUZE_LABELS[keuze] : 'Nog geen advies'
}

// batch-70 (FO-530) — bronlijst en certificaat-vinkmodel (eigen module, via deze export).
export * from './recruitment-sollicitant'
